'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  ChevronRight,
  CreditCard,
  Download,
  Grid3X3,
  ImageIcon,
  Loader2,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { Link } from '@/core/i18n/navigation';
import { AIMediaType, AITaskStatus } from '@/extensions/ai/types';
import {
  ImageUploader,
  ImageUploaderValue,
  LazyImage,
} from '@/shared/blocks/common';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Progress } from '@/shared/components/ui/progress';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { useAppContext } from '@/shared/contexts/app';
import { trackPhotoEffects } from '@/shared/lib/analytics';
import { cn } from '@/shared/lib/utils';

export interface PhotoEffect {
  id: string;
  name: string;
  preview: string;
  model: string;
  provider: string;
  prompt?: string;
}

interface PhotoEffectsGeneratorProps {
  defaultEffect?: string;
  effects?: PhotoEffect[];
  allowMultipleImages?: boolean;
  maxImages?: number;
  maxSizeMB?: number;
  srOnlyTitle?: string;
}

interface GeneratedImage {
  id: string;
  url: string;
  effect?: string;
  prompt?: string;
}

interface BackendTask {
  id: string;
  status: string;
  provider: string;
  model: string;
  prompt: string | null;
  taskInfo: string | null;
  taskResult: string | null;
}

const POLL_INTERVAL = 5000;
const GENERATION_TIMEOUT = 180000;

// Default model configuration
const DEFAULT_MODEL = 'bytedance/seedream-4';
const DEFAULT_PROVIDER = 'replicate';

// 默认特效配置 - 使用 seedream-4 模型 (replicate)
const DEFAULT_EFFECTS: PhotoEffect[] = [
  {
    id: 'ghibli',
    name: 'Ghibli Style',
    preview: 'https://r2.zestygen.com/effects/Ghibli-girl.png',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Transform this image into Studio Ghibli anime style. Apply hand-drawn animation aesthetic with soft pastel colors, dreamy watercolor backgrounds, gentle lighting, whimsical atmosphere. Maintain the original composition and subjects but render them in the distinctive Ghibli art style with expressive character features, detailed natural elements, and nostalgic magical atmosphere reminiscent of Hayao Miyazaki films like Spirited Away, My Neighbor Totoro, and Howl\'s Moving Castle.',
  },
  {
    id: 'rick-and-morty',
    name: 'Rick and Morty',
    preview: 'https://r2.zestygen.com/effects/rick-and-morty-style.png',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Rick and Morty style. Transform this image into Rick and Morty cartoon style from Adult Swim animated series. Apply bold black outlines, vibrant saturated colors, slightly droopy expressive eyes, exaggerated facial features. Render with the show\'s distinctive art style including uneven proportions, sharp angular shapes, and the characteristic interdimensional aesthetic. Style similar to characters from Rick and Morty with that schwifty cartoon energy.',
  },
  {
    id: 'y2k-style',
    name: 'Y2K Style',
    preview: 'https://r2.zestygen.com/effects/y2k-style2.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Y2K美学风格的竖屏时尚情绪板。背景是数码故障艺术风格的网格纸，带有全息镭射的粉紫配色。所有元素呈现高光泽的乙烯基贴纸质感，白边清晰。主角穿着Y2K风格服饰。Labubu公仔贴纸佩戴银色大耳机和金属配饰，造型前卫。隐藏层贴纸是一件性感的镂空紧身连体衣。周围散落着像素风的手绘星星、蝴蝶图案和电子宠物涂鸦。字体采用气泡立体字风格。整体画面色彩高饱和度，充满未来复古感，不仅是照片，更是一幅完整的数字波普艺术画作。',
  },
  {
    id: 'pop-art',
    name: 'Pop Art',
    preview: 'https://r2.zestygen.com/effects/pop-art.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: '主体：一个年轻女子，姿势为侧坐在街机凳上，抱着腿，做着俏皮的眨眼表情。**造型：**蓝绿色 T 恤，粉色短裤，紫色船袜，霓虹亮色运动鞋。发型为黑色编织双马尾，配有霓虹发圈。**风格：**极繁主义甜点怪兽波普艺术 (Maximalist Sweets-Monster Pop-Art)。环境/效果：复古街机内部，强烈的霓虹混合光照。画面被高密度的甜点怪物和霓虹图形（如甜甜圈、巧克力、香蕉幽灵、软糖熊、融化的冰淇淋、星星、心形、闪光）淹没。所有图形都带有粗黑轮廓和霓虹亮色。**关键限制：**插画元素（怪兽和图形）不能接触脸部、手臂或腿部，但要填满几乎所有背景和服装区域。高分辨率。',
  },
  {
    id: 'electric-graffiti',
    name: 'Electric Graffiti',
    preview: 'https://r2.zestygen.com/effects/electric-graffiti1.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: '核心指令： 将上传的原始图片主体（人物/对象）的样貌和特征完全保留。基础风格配置：介质 (Medium): 描绘在一张有横线的笔记本纸上，体现出真实墨水吸收、分层笔压、纸张边缘污渍、污迹、以及细微的纸张褶皱等纹理细节。工具 (Tools): 使用圆珠笔、霓虹荧光笔和墨水进行创作。艺术风格 (Art Style): 涂鸦艺术 (Doodle Art)、漫画注解和速写 (Sketch) 的混合风格。线条 (Line-work): 粗细变化、随性自由式、凌乱笔触，并带有动态的阴影线 (Dynamic Hatch Shading)。氛围 (Atmosphere): 混乱 (Chaotic)、充满能量 (Energetic)、自发性 (Spontaneous)、密集 (Dense)。构图与元素：构图 (Framing): 肖像构图，头部周围有粗线条边框。周边元素 (Surrounding Elements): 周围布满凌乱的箭头、星星、下划线、随机的涂鸦、对话气泡、重叠的注释和复选框 (Checkboxes)。标志符号 (Iconography): 包含闪电、灯泡、音符和迷你自画像/Q版形象等符号。排版 (Typography): 使用手写漫画笔记风格的文字，例如包含拟声词："ZAP!" 和 "WHOOSH!"。渲染与细节：分辨率 (Resolution): 4K 高清渲染。细节水平 (Detail Level): 高细节 (High Detail)。光照 (Lighting): 大胆的外发光 (Bold Outer Glow)，鲜明的对比度 (Vibrant Contrast)。可选风格变体（建议生成时尝试不同变体或作为提示词补充）：电光涂鸦 (Electric Graffiti): 采用炙热的电光蓝和霓虹红，呈现涂鸦风格、夸张的轮廓和俏皮的高光。',
  },
  {
    id: 'action-figure',
    name: 'Action Figure',
    preview: 'https://r2.zestygen.com/effects/ai-action-figure-generator1.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Create a 1/7 scale commercialized figurine of the characters in the picture, in a realistic style, in a real environment. The figurine is placed on a computer desk. The figurine has a round transparent acrylic base, with no text on the base. The content on the computer screen is a 3D modeling process of this figurine. Next to the computer screen is a toy packaging box, designed in a style reminiscent of high-quality collectible figures, printed with original artwork. The packaging features two-dimensional flat illustrations.',
  },
  {
    id: 'gta',
    name: 'GTA Poster',
    preview: 'https://r2.zestygen.com/effects/ai-gta-generator.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Convert the uploaded image into a "Grand Theft Auto" (GTA) poster style. The image should resemble the iconic GTA cover art, with bold, vivid colors, sharp outlines, and a slightly comic book-like aesthetic. Ensure that the characters and objects in the image are stylized with a high level of detail, using shading and highlights typical of GTA artwork. The background should be simplified but still retain key details in the GTA art style. Keep the composition balanced and visually appealing, ensuring the subject stands out clearly.',
  },
  {
    id: 'vector-art',
    name: 'Vector Art',
    preview: 'https://r2.zestygen.com/effects/ai-vector-art.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Transform the uploaded image into a high-quality vector-style illustration. Ensure the final image retains the key features, details, and essence of the original, while presenting it in a clean, geometric, and flat vector art style. Emphasize smooth lines, sharp edges, vibrant but balanced colors, and a minimalistic yet modern design.',
  },
  {
    id: 'photo-to-painting',
    name: 'Photo to Painting',
    preview: 'https://r2.zestygen.com/effects/ai-photo-to-painting.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Transform the given photo into an oil painting style artwork. Focus on replicating the colors, textures, and brush strokes typical of a traditional oil painting. Pay attention to the details that define the subjects in the photo, ensuring the final piece retains the essence of the image while adopting the aesthetic of an oil painting. Include soft blending and bold highlights, as well as a rich color palette that enhances the vibrancy of the subjects. Consider the composition and layout carefully, while also creating a sense of depth and dimension as seen in classic oil paintings.',
  },
  {
    id: 'ai-christmas-generator',
    name: 'AI Christmas Generator',
    preview: 'https://r2.zestygen.com/effects/ai-christmas-generator.webp',
    model: 'bytedance/seedram4',
    provider: DEFAULT_PROVIDER,
    prompt: '[全局指令]： 一个3x3的网格拼贴画，所有9个格子里必须是完全同一位女性。严格保持与参考图一致的面部特征。不要改变她的五官，只改变她的表情、妆容和造型以匹配各电影主题。格1：《真爱至上》风格-机场告别场景戴圣诞帽，手持包装礼物，温情拥抱，背景机场巨型圣诞树和彩灯装饰，柔和光线格2：《小鬼当家》风格-惊讶夸张表情手捂脸颊，戴歪圣诞帽，背景家中圣诞树、礼物堆和装饰彩灯，喜剧效果格3：《极地特快》风格-火车窗前戴睡帽，手持热可可配糖果拐杖，窗外魔幻雪景和圣诞村庄，梦幻蓝金色调格4：《圣诞怪杰》风格-真实人物绿色调服装配红色圣诞帽，搞怪表情，手持偷来的圣诞装饰，背景圣诞村彩灯和礼物，创意造型格5：《34街的奇迹》风格-复古百货商店场景穿经典红色圣诞礼服，手持圣诞购物袋，背景巨型圣诞树和复古装饰，经典好莱坞照明（中心）格6：《圣诞精灵》风格-完整精灵装扮（绿色上衣、红色条纹袜、尖头帽配铃铛），欢乐跳跃姿态，手持糖果拐杖，背景糖果色圣诞工坊格7：《冰雪奇缘》风格-冰蓝色公主裙配雪花皇冠，手持魔法冰晶，雪花飞舞，背景冰晶城堡和大大的圣诞树，特别的喜庆糖果，迪士尼魔法感格8：《真实的谎言》风格-黑色礼服配红色圣诞饰品，手持香槟，背景圣诞派对场景彩灯和装饰球，优雅神秘格9：《Last Christmas》风格-时尚女性穿红色复古大衣配绿色围巾和金色耳环，手持圣诞礼物盒，站在伦敦圣诞街市彩灯下，背景科文特花园巨型圣诞树和节日橱窗，温暖夜景灯光，充满希望的表情，现代浪漫电影美学每格模仿对应电影的色调、光线和氛围，圣诞装饰元素极其明显，电影海报构图，添加电影感标题文字效果，专业电影剧照质感，4K高清',
  },
  {
    id: 'ai-digital-art',
    name: 'AI Digital Art',
    preview: 'https://r2.zestygen.com/effects/ai-digital-art.webp',
    model: 'bytedance/seedram4',
    provider: DEFAULT_PROVIDER,
    prompt: 'Using the uploaded image for accurate facial likeness, create a surreal, modern digital artwork featuring the same person standing on top of an oversized smartphone displaying a music-player interface. Do not change the face. The person wears a white short-sleeve button-up shirt, black shorts, white sneakers, and large over-ear headphones, with sunglasses, standing casually with hands in pockets. The smartphone beneath appears massive, making the person look miniaturized, and shows a playlist screen with album covers and playback controls. The overall aesthetic is minimal, clean, and softly lit with a beige background. The style is cinematic, high-resolution, polished, and resembles a professional poster design. Face and body must 100% match the uploaded reference image',
  },
  {
    id: 'ai-minecraft-generator',
    name: 'AI Minecraft Generator',
    preview: 'https://r2.zestygen.com/effects/ai-minecraft-generator.webp',
    model: 'bytedance/seedram4',
    provider: DEFAULT_PROVIDER,
    prompt: 'A close-up illustration of the main subject from the input image, rendered in the hyper-stylized aesthetic of **Minecraft**. The character\'s **face and facial features must be strictly retained** and mapped onto the **blocky, pixelated, cubic design** typical of the game, while **maintaining the subject\'s original pose and expression**. The character should appear as a ** figure** in the blocky Minecraft style. Place this blocky character in a **dark, heavily wooded Minecraft forest environment**, featuring square trees, pixelated leaves, blocky terrain, and atmospheric shadows. **Style Keywords:** Minecraft style, blocky, pixelated, cubic, 8-bit, low-resolution texture, video game screenshot, low detail. **Constraint:** Prioritize the subject\'s identity and facial structure from the input image. Ensure a dark and moody atmosphere.',
  },
  {
    id: 'ai-puppet',
    name: 'AI Puppet',
    preview: 'https://r2.zestygen.com/effects/ai-puppet.webp',
    model: 'bytedance/seedram4',
    provider: DEFAULT_PROVIDER,
    prompt: 'Using the uploaded image for accurate facial likeness, create a surreal, modern digital artwork featuring the same person standing on top of an oversized smartphone displaying a music-player interface. Do not change the face. The person wears a white short-sleeve button-up shirt, black shorts, white sneakers, and large over-ear headphones, with sunglasses, standing casually with hands in pockets. The smartphone beneath appears massive, making the person look miniaturized, and shows a playlist screen with album covers and playback controls. The overall aesthetic is minimal, clean, and softly lit with a beige background. The style is cinematic, high-resolution, polished, and resembles a professional poster design. Face and body must 100% match the uploaded reference image',
  },
  {
    id: 'ai-pet-birthday',
    name: 'AI Pet Birthday',
    preview: 'https://r2.zestygen.com/effects/ai-pet-birthday.webp',
    model: 'bytedance/seedram4',
    provider: DEFAULT_PROVIDER,
    prompt: 'A cheerful birthday celebration scene for the pet, based on the original image. The pet is wearing a colorful birthday hat, with a decorated birthday cake featuring lit candles placed nearby. The environment includes festive party decorations such as balloons, confetti, and streamers. Keep the pet\'s appearance and pose consistent with the uploaded image, while enhancing the scene with warm lighting and a joyful atmosphere to make it look realistic and engaging',
  },
  {
    id: 'ai-pet-meme',
    name: 'AI Pet Meme',
    preview: 'https://r2.zestygen.com/effects/ai-pet-meme.webp',
    model: 'bytedance/seedram4',
    provider: DEFAULT_PROVIDER,
    prompt: 'Turn this photo of my [cat/dog] into a funny hand-drawn WeChat sticker . Style : Minimalist ugly-cute line drawing (doodle style). White background. Expression : Exaggerate the animal\'s expression to look extremely shocked/judgemental/lazy (based on photo). Accessories : Add cute little doodles like sweat drops, question marks, or sparkles around the head. Text : Add handwritten text at the bottom: \'So Dumb\'. Ensure the text style is messy and funny.',
  },
  {
    id: 'ai-christmas-elf',
    name: 'AI Christmas Elf',
    preview: 'https://r2.zestygen.com/effects/ai-christmas-elf-effests.webp',
    model: 'bytedance/seedram4',
    provider: DEFAULT_PROVIDER,
    prompt: '(杰作, 最高画质, 超细节, 8k分辨率). 一张照片般逼真的4格分屏拼图，所有画面为同一女性角色。[关键：保持精确的面部特征，保留原始脸部结构，整个拼图中角色完全一致]. 角色皮肤白皙，质感自然，眼神明亮。左上图：角色穿着绿色的圣诞精灵服装，戴着尖尖的精灵耳朵道具，对着镜头敬礼，表情顽皮。右上图：角色手里拿着一个巨大的玩具锤子，假装要敲打镜头，眼睛睁得圆圆的。左下图：角色正在包装礼物，嘴里咬着丝带的一端，眉头微皱显得很专注可爱。右下图：角色坐在礼物堆上，双手托腮，双脚悬空晃动，一脸满足。环境：色彩饱和的圣诞工坊背景，红绿撞色。灯光：明亮的影棚灯光，无阴影，卡通感强。风格：K-pop专辑内页风格，色彩鲜艳跳跃，清晰对焦，活泼搞怪。',
  },
  {
    id: '3d-dartoon-keychain',
    name: '3D Dartoon Keychain',
    preview: 'https://r2.zestygen.com/effects/3d-dartoon-keychain-effects.webp',
    model: 'bytedance/seedram4',
    provider: DEFAULT_PROVIDER,
    prompt: 'Use uploaded image as facial reference, preserve original face and identity exactly. Create a caricature-style keychain character, gender-neutral, riding a yellow scooter indoors. The character has an oversized head with joyful, playful smile. Outfit: yellow knit beanie, striped yellow-black sweater, denim shorts, white socks, white sneakers. Keychain detail: blue strap labeled "SAMMU". Lighting: soft indoor lighting. Depth of field: shallow depth of field. Background: mall-like indoor environment. Style: whimsical, toy-like, premium collectible. Photography: cinematic product photography. Texture: smooth plastic, high detail finish.',
  },
  {
    id: 'ai-nintendo',
    name: 'AI Nintendo',
    preview: 'https://r2.zestygen.com/effects/ai-nintendo-effects.webp',
    model: 'bytedance/seedram4',
    provider: DEFAULT_PROVIDER,
    prompt: 'Transform this image into a Nintendo-inspired 3D cartoon style illustration. Use soft, rounded 3D shapes and clean, toy-like geometry to give the characters and objects a charming, handcrafted look. Characters should have exaggerated, childlike proportions (large heads, small limbs), with expressive, simplified faces and bright, colorful clothing — similar to designs seen in Zelda: Link\'s Awakening, Animal Crossing, or Miitopia. Apply smooth, matte textures with no realistic detail — everything should look cheerful and slightly plastic, as if sculpted from soft clay or digital vinyl. The environment should be bright and whimsical, with stylized grass, puffy clouds, geometric trees, and soft lighting like a sunny afternoon. The overall tone should be lighthearted, clean, and family-friendly, like a frame from a Nintendo fantasy adventure game.',
  },
  {
    id: 'ai-poster-effects',
    name: 'AI Poster Effects',
    preview: 'https://r2.zestygen.com/effects/ai-poster-effects-effects.webp',
    model: 'bytedance/seedram4',
    provider: DEFAULT_PROVIDER,
    prompt: 'A hyper-realistic cinematic movie poster of a powerful female sorcerer with the same facial structure and likeness as the uploaded reference photo, bursting through a cracked Queen of Spades playing card. The card explodes outward with stone fragments, dust, and debris frozen mid-air. She wears an ornate royal maroon and gold embroidered medieval fantasy jacket, rich fabric textures, intricate detailing, regal and mystical. The sorcerer extends one hand forward toward the viewer, fingers glowing with intense magical energy, subtle golden sparks and dark arcane aura surrounding the hand. Intense piercing gaze, confident and dominant expression, cinematic hero framing. Dramatic chiaroscuro lighting, dark moody background, volumetric light rays, ultra-detailed textures, shallow cinematic depth of field. Photorealistic face, epic fantasy realism, movie poster composition, high contrast, dynamic motion, dust particles, masterpiece quality, ultra-sharp focus, 8K resolution, cinematic color grading.',
  },
];

function parseTaskResult(taskResult: string | null): any {
  if (!taskResult) {
    return null;
  }

  try {
    return JSON.parse(taskResult);
  } catch (error) {
    console.warn('Failed to parse taskResult:', error);
    return null;
  }
}

function extractImageUrls(result: any): string[] {
  if (!result) {
    return [];
  }

  const output = result.output ?? result.images ?? result.data;

  if (!output) {
    return [];
  }

  if (typeof output === 'string') {
    return [output];
  }

  if (Array.isArray(output)) {
    return output
      .flatMap((item) => {
        if (!item) return [];
        if (typeof item === 'string') return [item];
        if (typeof item === 'object') {
          const candidate =
            item.url ?? item.uri ?? item.image ?? item.src ?? item.imageUrl;
          return typeof candidate === 'string' ? [candidate] : [];
        }
        return [];
      })
      .filter(Boolean);
  }

  if (typeof output === 'object') {
    const candidate =
      output.url ?? output.uri ?? output.image ?? output.src ?? output.imageUrl;
    if (typeof candidate === 'string') {
      return [candidate];
    }
  }

  return [];
}

export function PhotoEffectsGenerator({
  defaultEffect,
  effects = DEFAULT_EFFECTS,
  allowMultipleImages = false,
  maxImages = 1,
  maxSizeMB = 5,
  srOnlyTitle,
}: PhotoEffectsGeneratorProps) {
  const t = useTranslations('photo-effects.common.generator');

  const [selectedEffect, setSelectedEffect] = useState<string>(
    defaultEffect || effects[0]?.id || ''
  );
  const [costCredits, setCostCredits] = useState<number>(1);
  const [referenceImageItems, setReferenceImageItems] = useState<
    ImageUploaderValue[]
  >([]);
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(
    null
  );
  const [taskStatus, setTaskStatus] = useState<AITaskStatus | null>(null);
  const [downloadingImageId, setDownloadingImageId] = useState<string | null>(null);
  const [showAllEffectsModal, setShowAllEffectsModal] = useState(false);

  const { user, isCheckSign, setIsShowSignModal, fetchUserCredits } =
    useAppContext();

  // Reorder effects to put defaultEffect first if it exists
  const orderedEffects = useMemo(() => {
    if (!defaultEffect) return effects;
    
    const defaultIndex = effects.findIndex((e) => e.id === defaultEffect);
    if (defaultIndex === -1) return effects;
    
    // Move the default effect to the front
    const reordered = [...effects];
    const [defaultEffectItem] = reordered.splice(defaultIndex, 1);
    return [defaultEffectItem, ...reordered];
  }, [defaultEffect, effects]);

  const currentEffect = orderedEffects.find((e) => e.id === selectedEffect);
  const remainingCredits = user?.credits?.remainingCredits ?? 0;

  const handleGenerate = async () => {
    if (referenceImageUrls.length === 0) {
      toast.error(t('errors.reference-image-required'));
      return;
    }

    if (!currentEffect) {
      toast.error(t('errors.effect-required'));
      return;
    }

    if (isCheckSign) {
      setIsShowSignModal(true);
      return;
    }

    if (remainingCredits < costCredits) {
      toast.error(t('errors.insufficient-credits'));
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setTaskStatus(null);
    setGenerationStartTime(Date.now());

    try {
      // Use image-to-image scene with reference images (same as ImageGenerator)
      const options: any = {
        image_input: referenceImageUrls,
      };

      const response = await fetch('/api/ai/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mediaType: AIMediaType.IMAGE,
          scene: 'photo-effects', // Special scene for photo effects (1 credit)
          provider: currentEffect.provider,
          model: currentEffect.model,
          prompt: currentEffect.prompt,
          options,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('errors.generation-failed'));
      }

      const { code, message, data } = await response.json();
      if (code !== 0) {
        throw new Error(message || t('errors.generation-failed'));
      }

      const newTaskId = data?.id;
      if (!newTaskId) {
        throw new Error('Task id missing in response');
      }

      // Check if task completed immediately
      if (data.status === AITaskStatus.SUCCESS && data.taskInfo) {
        const parsedResult = parseTaskResult(data.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (imageUrls.length > 0) {
          const timestamp = Date.now();
          const newImages: GeneratedImage[] = imageUrls.map((url, index) => ({
            id: `${newTaskId}-${timestamp}-${index}`,
            url,
            effect: selectedEffect,
            prompt: currentEffect.prompt || undefined,
          }));

          setGeneratedImages((prev) => [...newImages, ...prev]);
          toast.success(t('success.image-generated'));

          // Track photo effects for analytics
          trackPhotoEffects({
            effect: selectedEffect,
            credits_used: costCredits,
          });

          setIsGenerating(false);
          setProgress(100);
          setGenerationStartTime(null);
          setTaskStatus(null);
          await fetchUserCredits();
          return;
        }
      }

      setTaskId(newTaskId);
      await fetchUserCredits();
    } catch (error: any) {
      console.error('Image generation error:', error);
      toast.error(error.message || t('errors.generation-failed'));
      setIsGenerating(false);
      setProgress(0);
      setTaskStatus(null);
      setGenerationStartTime(null);
    }
  };

  useEffect(() => {
    if (!taskId || !isGenerating) {
      return;
    }

    let cancelled = false;

    const pollTask = async (): Promise<boolean> => {
      try {
        // Check for timeout
        if (
          generationStartTime &&
          Date.now() - generationStartTime > GENERATION_TIMEOUT
        ) {
          toast.error(t('errors.generation-timeout'));
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        const response = await fetch('/api/ai/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ taskId }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch task status');
        }

        const { code, message, data } = await response.json();
        if (code !== 0) {
          throw new Error(message || 'Query task failed');
        }

        const task = data as BackendTask;
        const currentStatus = task.status as AITaskStatus;
        setTaskStatus(currentStatus);

        const parsedResult = parseTaskResult(task.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (currentStatus === AITaskStatus.PENDING) {
          setProgress((prev) => Math.max(prev, 20));
          return false;
        }

        if (currentStatus === AITaskStatus.PROCESSING) {
          if (imageUrls.length > 0) {
            setProgress((prev) => Math.max(prev, 85));
          } else {
            setProgress((prev) => Math.min(prev + 10, 80));
          }
          return false;
        }

        if (currentStatus === AITaskStatus.SUCCESS) {
          if (imageUrls.length === 0) {
            toast.error(t('errors.no-image-url'));
          } else {
            const timestamp = Date.now();
            const newImages: GeneratedImage[] = imageUrls.map((url, index) => ({
              id: `${taskId}-${timestamp}-${index}`,
              url,
              effect: selectedEffect,
              prompt: task.prompt || undefined,
            }));

            setGeneratedImages((prev) => [...newImages, ...prev]);
            toast.success(t('success.image-generated'));

            // Track photo effects for analytics
            trackPhotoEffects({
              effect: selectedEffect,
              credits_used: costCredits,
            });
          }

          setProgress(100);
          setIsGenerating(false);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        if (currentStatus === AITaskStatus.FAILED) {
          const errorMessage = parsedResult?.errorMessage || t('errors.generation-failed');
          toast.error(errorMessage);
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          fetchUserCredits();
          return true;
        }

        setProgress((prev) => Math.min(prev + 5, 95));
        return false;
      } catch (error: any) {
        console.error('Error polling task status:', error);
        toast.error(`Query task failed: ${error.message}`);
        setIsGenerating(false);
        setProgress(0);
        setTaskId(null);
        setGenerationStartTime(null);
        setTaskStatus(null);
        fetchUserCredits();
        return true;
      }
    };

    // Initial poll
    pollTask();

    // Set up interval
    const interval = setInterval(async () => {
      if (cancelled || !taskId) {
        clearInterval(interval);
        return;
      }
      const completed = await pollTask();
      if (completed) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [taskId, isGenerating, generationStartTime, selectedEffect, t, fetchUserCredits]);

  const handleDownloadImage = async (image: GeneratedImage) => {
    if (!image.url) {
      return;
    }

    try {
      setDownloadingImageId(image.id);
      // Fetch image via proxy to avoid CORS issues
      const response = await fetch(
        `/api/proxy/file?url=${encodeURIComponent(image.url)}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `zestygen-${image.effect}-${image.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
      toast.success(t('success.image-downloaded'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('errors.download-failed'));
    } finally {
      setDownloadingImageId(null);
    }
  };

  const handleSelectEffect = (effectId: string) => {
    setSelectedEffect(effectId);
    setShowAllEffectsModal(false);
  };

  return (
    <section className="py-16 md:py-24">
      <div className="container">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_minmax(0,1fr)_minmax(0,1fr)]">
            {/* Effects Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  {t('effects.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  <div className="grid grid-cols-2 gap-3">
                    {orderedEffects.map((effect) => {
                      const isSelected = selectedEffect === effect.id;
                      return (
                        <button
                          key={effect.id}
                          onClick={() => setSelectedEffect(effect.id)}
                          className={cn(
                            'group relative overflow-hidden rounded-lg transition-all duration-200',
                            isSelected
                              ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                              : 'hover:ring-2 hover:ring-muted-foreground/30 hover:ring-offset-1 hover:ring-offset-background'
                          )}
                        >
                          <div className="relative aspect-square overflow-hidden rounded-t-lg">
                            <img
                              src={effect.preview}
                              alt={effect.name}
                              className={cn(
                                'h-full w-full object-cover transition-transform duration-300',
                                isSelected ? 'scale-105' : 'group-hover:scale-105'
                              )}
                            />
                            {/* Selected overlay */}
                            {isSelected && (
                              <div className="absolute inset-0 bg-primary/20" />
                            )}
                            {/* Selected checkmark */}
                            {isSelected && (
                              <div className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                                <Check className="h-3 w-3" />
                              </div>
                            )}
                          </div>
                          <div
                            className={cn(
                              'p-1.5 text-center text-xs font-medium transition-colors',
                              isSelected
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted/50 group-hover:bg-muted'
                            )}
                          >
                            {effect.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>

                {/* More Effects Button */}
                <Dialog open={showAllEffectsModal} onOpenChange={setShowAllEffectsModal}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="mt-4 w-full"
                      size="sm"
                    >
                      <Grid3X3 className="mr-2 h-4 w-4" />
                      {t('effects.browse-more')}
                      <ChevronRight className="ml-auto h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[80vh]">
                    <DialogHeader>
                      <DialogTitle>{t('effects.all-effects')}</DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="h-[60vh] pr-4">
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                        {effects.map((effect) => {
                          const isSelected = selectedEffect === effect.id;
                          return (
                            <button
                              key={effect.id}
                              onClick={() => handleSelectEffect(effect.id)}
                              className={cn(
                                'group relative overflow-hidden rounded-xl transition-all duration-200',
                                isSelected
                                  ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                                  : 'hover:ring-2 hover:ring-muted-foreground/30'
                              )}
                            >
                              <div className="relative aspect-square overflow-hidden rounded-t-xl">
                                <img
                                  src={effect.preview}
                                  alt={effect.name}
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                />
                                {isSelected && (
                                  <>
                                    <div className="absolute inset-0 bg-primary/20" />
                                    <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                                      <Check className="h-4 w-4" />
                                    </div>
                                  </>
                                )}
                              </div>
                              <div
                                className={cn(
                                  'p-2 text-center text-sm font-medium transition-colors',
                                  isSelected
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted/50 group-hover:bg-muted'
                                )}
                              >
                                {effect.name}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            {/* Generator Controls */}
            <Card>
              <CardHeader>
                {srOnlyTitle && <h2 className="sr-only">{srOnlyTitle}</h2>}
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  {t('title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 pb-8">
                {/* Default Preview - Show selected effect preview when no image uploaded */}
                {referenceImageUrls.length === 0 && currentEffect && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">{t('labels.effect-preview')}</Label>
                    <div className="relative overflow-hidden rounded-lg border-2 border-dashed border-muted-foreground/30">
                      <img
                        src={currentEffect.preview}
                        alt={currentEffect.name}
                        className="h-auto w-full object-cover opacity-70"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-background/50">
                        <p className="text-center text-sm text-muted-foreground px-4">
                          {t('labels.upload-hint')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{t('labels.reference-image')}</Label>
                  <ImageUploader
                    defaultPreviews={referenceImageUrls}
                    onChange={(items) => {
                      setReferenceImageItems(items);
                      const urls = items
                        .filter((item) => item.status === 'uploaded' && item.url)
                        .map((item) => item.url!);
                      setReferenceImageUrls(urls);
                    }}
                    allowMultiple={allowMultipleImages}
                    maxImages={maxImages}
                    maxSizeMB={maxSizeMB}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {t('labels.cost')}
                    </span>
                  </div>
                  <span className="text-sm font-semibold">
                    {costCredits} {t('labels.credits')}
                  </span>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {t('labels.remaining-credits')}
                    </span>
                  </div>
                  <span className="text-sm font-semibold">{remainingCredits}</span>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={
                    isGenerating ||
                    referenceImageUrls.length === 0 ||
                    !currentEffect
                  }
                  className="w-full"
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('buttons.generating')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t('buttons.generate')}
                    </>
                  )}
                </Button>

                {isGenerating && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t('labels.progress')}
                      </span>
                      <span className="font-medium">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <Progress value={progress} />
                    {taskStatus && (
                      <p className="text-center text-xs text-muted-foreground">
                        {t(`status.${taskStatus.toLowerCase()}`)}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  <ImageIcon className="h-5 w-5" />
                  {t('results.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generatedImages.length === 0 ? (
                  <div className="relative min-h-[400px] overflow-hidden rounded-lg border-2 border-dashed">
                    {/* Show effect preview as placeholder */}
                    {currentEffect && (
                      <img
                        src={currentEffect.preview}
                        alt={currentEffect.name}
                        className="absolute inset-0 h-full w-full object-cover opacity-30"
                      />
                    )}
                    <div className="relative flex min-h-[400px] items-center justify-center bg-background/60">
                      <div className="text-center">
                        <ImageIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                        <p className="mt-4 text-sm text-muted-foreground">
                          {t('results.no-images')}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground/70">
                          {t('results.preview-hint')}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <ScrollArea className="h-[600px] pr-4">
                    <div className="space-y-4">
                      {generatedImages.map((image) => (
                        <div
                          key={image.id}
                          className="group relative overflow-hidden rounded-lg border"
                        >
                          <LazyImage
                            src={image.url}
                            alt={`Generated with ${image.effect}`}
                            className="w-full"
                          />
                          <div className="absolute right-2 top-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleDownloadImage(image)}
                              disabled={downloadingImageId === image.id}
                            >
                              {downloadingImageId === image.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
